import { useEffect, useState, type FormEvent } from 'react';
import { koreaDate, monthEnd } from '@chagok/domain';
import { api, ApiError, newId } from '../api/client';
import type { Budgets, Dashboard } from '../api/types';
import { krw, parseAmountInput, percent } from '../lib/format';
import { useAsync } from '../lib/useAsync';
import { useApp } from '../state';
import { ErrorBox } from './Dashboard';

/**
 * 월 예산: 이 달부터 다음 변경 전까지 같은 금액이 적용된다. 비워 두면 이 달부터 예산 없음.
 * 저장은 If-Match(이 달 revision)로 보내 다른 기기의 변경을 덮어쓰지 않는다.
 */
export function BudgetsPage() {
  const { dataVersion, changed, toast } = useApp();
  const [month, setMonth] = useState(koreaDate().slice(0, 7));
  const budgets = useAsync(() => api.get<{ data: Budgets }>('/budgets', { month }).then((r) => r.data), [month, dataVersion]);
  const usage = useAsync(() => api.get<{ data: Dashboard }>('/dashboard', { from: `${month}-01`, to: monthEnd(month) }).then((r) => r.data), [month, dataVersion]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<Budgets | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!budgets.data) return;
    const next: Record<string, string> = { total: budgets.data.total.amountKrw ?? '' };
    for (const c of budgets.data.categories) next[c.categoryId] = c.amountKrw ?? '';
    setValues(next);
    setConflict(null);
  }, [budgets.data]);

  if (budgets.error && !budgets.data) return <ErrorBox message={budgets.error.message} retry={budgets.reload} />;
  const b = !budgets.loading && budgets.data?.month === month ? budgets.data : null;

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!b || busy || conflict) return;
    const items: { categoryId: string | null; amountKrw: string | null }[] = [];
    const scopes = [{ id: null as string | null, key: 'total', current: b.total.amountKrw }, ...b.categories.map((c) => ({ id: c.categoryId as string | null, key: c.categoryId, current: c.amountKrw }))];
    for (const scope of scopes) {
      const raw = (values[scope.key] ?? '').trim();
      const amount = raw === '' ? null : parseAmountInput(raw);
      if (raw !== '' && !amount) return setError('예산은 1원 이상의 정수로 입력하거나 비워두세요.');
      if (amount !== scope.current) items.push({ categoryId: scope.id, amountKrw: amount });
    }
    if (!items.length) return setError('바뀐 금액이 없습니다.');
    setBusy(true);
    setError(null);
    try {
      await api.put('/budgets', { month, items }, { ifMatch: b.revision, idempotencyKey: newId() });
      toast(`${month}부터 적용할 예산을 저장했습니다.`);
      changed();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'REVISION_CONFLICT') setConflict((e.details as { current: Budgets }).current);
      else setError(e instanceof ApiError ? e.message : '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  const spent = (categoryId: string | null) =>
    categoryId === null ? (usage.data?.totals.netExpenseKrw ?? '0') : (usage.data?.expenseByCategory.find((c) => c.categoryId === categoryId)?.netKrw ?? '0');
  const inherited = (setIn: string | null) => (setIn && setIn !== month ? `${setIn}에 정한 금액 적용 중` : setIn === month ? '이 달에 정함' : '예산 없음');

  return (
    <section>
      <form className="card" onSubmit={(e) => void save(e)}>
        <div className="card-head">
          <div>
            <h2>월 예산</h2>
            <p className="muted">정한 달부터 다음에 바꿀 때까지 같은 금액이 적용됩니다. 칸을 비우면 이 달부터 예산을 해제합니다.</p>
          </div>
          <label className="inline">
            기준 월
            <input type="month" disabled={busy} value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
          </label>
        </div>
        {conflict && (
          <div className="conflict" role="alert">
            <b>다른 기기에서 먼저 예산을 바꿨습니다.</b>
            <p className="hint">
              서버의 최신 전체 예산: {conflict.total.amountKrw ? krw(conflict.total.amountKrw) : '없음'}. 덮어쓰지 않았습니다.
            </p>
            <button type="button" onClick={budgets.reload}>
              최신 값 불러오기
            </button>
          </div>
        )}
        {!b ? (
          <div className="empty">불러오는 중…</div>
        ) : (
          <div className="budget-table">
            <BudgetRow label="전체 예산" hint={inherited(b.total.setInMonth)} value={values.total ?? ''} onChange={(v) => setValues({ ...values, total: v })} spent={spent(null)} budget={b.total.amountKrw} strong />
            {b.categories.map((c) => (
              <BudgetRow
                key={c.categoryId}
                label={`${c.name}${c.archived ? ' (삭제됨)' : ''}`}
                hint={inherited(c.setInMonth)}
                value={values[c.categoryId] ?? ''}
                onChange={(v) => setValues({ ...values, [c.categoryId]: v })}
                spent={spent(c.categoryId)}
                budget={c.amountKrw}
                disabled={c.archived && !c.amountKrw}
              />
            ))}
          </div>
        )}
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <button className="primary save" disabled={busy || !b || !!conflict}>
          {busy ? '저장 중…' : `${month}부터 적용`}
        </button>
        <p className="hint">사용액은 순지출(지출 − 환불) 기준입니다. 대시보드에서 기간 일부만 조회하면 일수 비율로 ‘기간 환산 예산’을 계산합니다.</p>
      </form>
    </section>
  );
}

function BudgetRow(props: { label: string; hint: string; value: string; onChange: (v: string) => void; spent: string; budget: string | null; strong?: boolean; disabled?: boolean }) {
  const ratio = props.budget ? percent(props.spent, props.budget) : 0;
  return (
    <div className={`budget-row ${props.strong ? 'strong' : ''}`}>
      <div>
        <b>{props.label}</b>
        <small>{props.hint}</small>
      </div>
      <input inputMode="numeric" value={props.value} onChange={(e) => props.onChange(e.target.value)} placeholder="예산 없음" aria-label={`${props.label} 예산`} disabled={props.disabled} />
      <div className="budget-usage">
        <small>
          사용 {krw(props.spent)}
          {props.budget ? ` · ${Math.round(ratio)}%` : ''}
        </small>
        {props.budget && (
          <div className="track">
            <i className={ratio > 100 ? 'over' : ''} style={{ width: `${Math.min(ratio, 100)}%` }} />
          </div>
        )}
      </div>
    </div>
  );
}
